import { MapPinIcon } from "@heroicons/react/24/solid";
import { FC, useCallback, useEffect, useState } from "react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Input from "shared/Input/Input";
import FormItem from "./FormItem";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "redux/store";
import { useFormik } from "formik";
import {
  createProperties,
  getallupdateProperties,
  setId,
  updateProperties,
} from "../../redux/reducers/Properties/PropertiesSlice";
import successHandler from "utils/helpers/SuccessHandler";
import { locationValidationSchema } from "utils/formSchema";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useSelector } from "react-redux";
import __countryListing from "../../data/jsons/__countryListing.json";
import CommonLayoutV2 from "./CommonLayoutV2";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
  Circle,
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { calculateStepCompletion, checkExistingCompletion } from "utils/helpers/listingCompletionHelper";
import "../../components/HeroSearchForm/LocationInput.scss";

export interface LocationProps {}

interface FormValues {
  title: string;
  country: string;
  street: string;
  city: string;
  state: string;
  zipcode: string | number;
  latitude: number | null;
  longitude: number | null;
  neighborhood: string; 
}

const Location: FC<LocationProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const { globalId, PropertyToEdit } = useSelector((state: RootState) => state.property);
  const { slug } = useParams();
  const id = slug;
  const RouteID = globalId || id;
  const [loading, setLoading] = useState<boolean>(false);
  const [address, setAddress] = useState<{
  street: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  neighborhood: string; // 🆕 ADD THIS
}>({ 
  street: "", 
  city: "", 
  state: "", 
  zipcode: "", 
  country: "",
  neighborhood: "" // 🆕 ADD THIS
});
  const [propertyToEdit, setPropertyToEdit] = useState<any>(null);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  }>({ lat: 38.9072, lng: -77.0369 });
  const [isEditing, setIsEditing] = useState(false);
  const [updateLocation, setUpdateLocation] = useState(false);
  const [googleAddress, setGoogleAddress] = useState("");

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false, fieldsCompleted: 0, totalFields: 6 },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false },
    9: { completed: false },
  });

  // Track form values for completion
  const [formValues, setFormValues] = useState<any>(null);
  const [formErrors, setFormErrors] = useState<any>({});

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
    libraries: ["places"],
  });

  useEffect(() => {
    const fetchProperty = async () => {
      const propertyId = id;
      if (propertyId) {
        const action = await dispatch(getallupdateProperties({ propertyId }));
        if (action?.payload?.success) {
          const { latitude, longitude, address } = action?.payload?.property;
          setUserLocation({ lat: Number(latitude), lng: Number(longitude) });
          setGoogleAddress(address);
          setPropertyToEdit(action?.payload?.property);
        }
      }
    };
    fetchProperty();
  }, [dispatch, globalId, id]);

  const initialValues = {
    title: propertyToEdit?.title || "",
    country: address?.country || propertyToEdit?.country || "",
    street: address?.street || propertyToEdit?.address || "",
    city: address?.city || propertyToEdit?.city || "",
    state: address?.state || propertyToEdit?.state || "",
    zipcode: address?.zipcode || propertyToEdit?.zipcode || "",
    latitude: userLocation?.lat || propertyToEdit?.latitude || null,
    longitude: userLocation?.lng || propertyToEdit?.longitude || null,
    neighborhood: address?.neighborhood || propertyToEdit?.neighborhood || "", 
  };

  const formik = useFormik<FormValues>({
    initialValues: initialValues,
    validationSchema: locationValidationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const payload = {
        address: `${values.street}`,
        city: values.city,
        state: values.state,
        country: values.country,
        zipcode: values.zipcode,
        latitude: values.latitude,
        longitude: values.longitude,
        neighborhood: values.neighborhood,
        ...(id ? { id: id } : {}),
      };

      setSubmitting(true);
      if (id) {
        if (isEditing) {
          dispatch(updateProperties({ propertyId: id, property: payload }))
            .unwrap()
            .then((response) => {
              if (response?.success) {
                successHandler(response?.message || "Updated successfully");
                navigate(`/add-listing-6/${slug}`);
                resetForm();
              } else {
                console.log("Update failed: ", response);
              }
            })
            .catch((error) => {
              console.log("Error: ", error);
            })
            .finally(() => {
              setSubmitting(false);
            });
        } else {
          navigate(`/add-listing-6/${slug}`);
        }
      } else {
        dispatch(createProperties({ property: payload }))
          .unwrap()
          .then((response) => {
            if (response?.success) {
              successHandler(response?.message || "Created successfully");
              dispatch(setId(response.property.id));
              navigate(`/add-listing-6?id=${response.property.id}`);
              resetForm();
            } else {
              console.log("Creation failed: ", response);
            }
          })
          .catch((error) => {
            console.log("Error: ", error);
          })
          .finally(() => {
            setSubmitting(false);
          });
      }
    },
  });

  const initialFormValues = JSON.stringify(initialValues);

  useEffect(() => {
    const currentFormValues = JSON.stringify(formik.values);
    if (updateLocation) {
      setIsEditing(true);
    } else {
      setIsEditing(currentFormValues !== initialFormValues);
    }
  }, [formik.values, initialFormValues, updateLocation]);

  // Calculate completion for Step 5
  useEffect(() => {
    if (formValues) {
      const requiredFields = ["street", "city", "state", "zipcode", "latitude", "longitude"];
      const hasErrors = Object.keys(formErrors).length > 0;
      const completion = calculateStepCompletion(formValues, requiredFields, hasErrors);

      setStepCompletionStatus((prev) => ({
        ...prev,
        5: completion,
      }));
    }
  }, [formValues, formErrors]);

  // Load completion status from PropertyToEdit
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);
      
      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  // Update form tracking
  useEffect(() => {
    if (JSON.stringify(formik.values) !== JSON.stringify(formValues)) {
      setFormValues(formik.values);
    }
    if (JSON.stringify(formik.errors) !== JSON.stringify(formErrors)) {
      setFormErrors(formik.errors);
    }
  }, [formik.values, formik.errors]);

 const fetchAddress = useCallback(
  async (latitude: number, longitude: number) => {
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`;
    try {
      const response = await fetch(geocodeUrl);
      const data = await response.json();
      if (data.status === "OK" && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;
        const formattedAddress = data.results[0].formatted_address;

        let city = "";
        let state = "";
        let zipCode = "";
        let country = "";
        let neighborhood = ""; // 🆕 ADD THIS

        addressComponents.forEach((component: any) => {
          // Neighborhood
          if (
            component.types.includes("neighborhood") ||
            component.types.includes("sublocality_level_1") ||
            component.types.includes("sublocality") ||
            component.types.includes("locality") // For Peru-style districts
          ) {
            if (!neighborhood) neighborhood = component.long_name;
          }
          
          // City
          if (component.types.includes("administrative_area_level_2")) {
            city = component.long_name;
          } else if (!city && component.types.includes("locality")) {
            city = component.long_name;
          }
          
          if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          }
          if (component.types.includes("postal_code")) {
            zipCode = component.long_name;
          }
          if (component.types.includes("country")) {
            country = component.long_name;
          }
        });

        setAddress({
          street: formattedAddress || `${city}, ${state}, ${country}`,
          city: city || "N/A",
          state: state || "N/A",
          zipcode: zipCode || "N/A",
          country: country || "N/A",
          neighborhood: neighborhood || "", // 🆕 ADD THIS
        });
        setGoogleAddress(formattedAddress || `${city}, ${state}, ${country}`);
      } else {
        toast.info("No Address found", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error fetching geocode data:", error);
    }
  },
  []
);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUpdateLocation(true);
          setUserLocation({ lat: latitude, lng: longitude });
          await fetchAddress(latitude, longitude);
          setLoading(false);
        },
        (error) => {
          console.error("Error get user location: ", error);
          setLoading(false);
        }
      );
    } else {
      setLoading(false);
      console.log("Geolocation is not supported by this browser");
    }
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="flex items-center justify-center space-x-2">
          <div className="w-10 h-10 border-4 border-t-4 border-gray-300 border-t-primary-6000 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

const handlePlaceSelect = async (place: any) => {
  try {
    if (!place || !place.value || !place.value.place_id) return;
    
    const placeId = place.value.place_id;
    
    // ✅ SET THE DISPLAY VALUE IMMEDIATELY
    setGoogleAddress(place.label || "");
    
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`
    );
    
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const geocodeData = await response.json();
    if (geocodeData.status !== "OK") throw new Error(`Google API Error: ${geocodeData.status}`);
    
    if (geocodeData.results?.length > 0) {
      const { lat, lng } = geocodeData.results[0].geometry.location;
      setUserLocation({ lat: lat, lng: lng });
      
      const addressComponents = geocodeData.results[0].address_components;
      const formattedAddress = geocodeData.results[0].formatted_address;

      let city = "";
      let state = "";
      let zipCode = "";
      let country = "";
      let neighborhood = "";

      // 🌍 UNIVERSAL EXTRACTION - Try multiple types
      addressComponents.forEach((component: any) => {
        // Neighborhood (try multiple types)
        if (
          component.types.includes("neighborhood") ||
          component.types.includes("sublocality_level_1") ||
          component.types.includes("sublocality")
        ) {
          neighborhood = component.long_name;
        }
        
        // City (prioritize admin_area_level_2 for international compatibility)
        if (component.types.includes("administrative_area_level_2")) {
          city = component.long_name;
        } else if (!city && component.types.includes("locality")) {
          city = component.long_name;
        }
        
        // State
        if (component.types.includes("administrative_area_level_1")) {
          state = component.long_name;
        }
        
        // Postal code
        if (component.types.includes("postal_code") || component.types.includes("postal_code_prefix")) {
          zipCode = component.long_name;
        }
        
        // Country
        if (component.types.includes("country")) {
          country = component.long_name;
        }
      });

      // 🔄 FALLBACK: Reverse geocode if no neighborhood found
      if (!neighborhood && lat && lng) {
        try {
          const reverseResponse = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=locality|sublocality|neighborhood&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`
          );
          const reverseData = await reverseResponse.json();
          
          if (reverseData.status === "OK" && reverseData.results?.length > 0) {
            for (const result of reverseData.results) {
              const components = result.address_components;
              
              // Look for locality (works for Peru, many countries)
              for (const component of components) {
                if (component.types.includes("locality")) {
                  neighborhood = component.long_name;
                  
                  // If locality was used for neighborhood, update city if needed
                  if (!city || city === neighborhood) {
                    const adminComp = components.find((c: any) => 
                      c.types.includes("administrative_area_level_2")
                    );
                    if (adminComp) {
                      city = adminComp.long_name;
                    }
                  }
                  break;
                }
              }
              if (neighborhood) break;
            }
          }
        } catch (err) {
          console.error("Reverse geocode failed:", err);
        }
      }

      console.log("📍 Final extracted data:", {
        neighborhood,
        city,
        state,
        zipCode,
        country,
        formattedAddress
      });

      // ✅ SET ALL DATA INCLUDING NEIGHBORHOOD
      setAddress({
        street: formattedAddress || `${city}, ${state}, ${country}`,
        city: city || "N/A",
        state: state || "N/A",
        zipcode: zipCode || "N/A",
        country: country || "N/A",
        neighborhood: neighborhood || "",
      });
      
      // ✅ UPDATE THE DISPLAY VALUE WITH FORMATTED ADDRESS
      setGoogleAddress(formattedAddress);
    }
  } catch (error) {
    console.error("❌ Geocoding error:", error);
  }
};

  return (
    <CommonLayoutV2 
      currentHref="/add-listing-5" 
      PropertyID={RouteID}
      propertyType={PropertyToEdit?.property_type?.name}
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Property Location
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Help guests find your property by providing accurate location details and pinpointing it on the map.
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-8">
          {/* Address Search Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                1
              </span>
              Find Your Address
            </h3>

            <div className="space-y-4">
              <ButtonSecondary 
                type="button" 
                onClick={getUserLocation}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Fetching location...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <MapPinIcon className="w-5 h-5" />
                    Use my current location
                  </span>
                )}
              </ButtonSecondary>

              <div className="relative">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Or search for an address:</p>
                <div className="border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-[#F56948] focus-within:border-[#F56948] transition-colors">
                 <GooglePlacesAutocomplete
  apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY || ""}
  selectProps={{
    value: googleAddress ? { label: googleAddress, value: googleAddress } : null,  // ✅ Changed from inputValue
    placeholder: "Enter your property address...",
    onChange: (place: any) => {
      if (place && place.value && place.value.place_id) {
        handlePlaceSelect(place);
        setUpdateLocation(true);
      }
    },
    onInputChange: (newValue: string) => {
      // This handles typing but won't interfere with selection
      return newValue;
    },
    className: "text-base",
    isClearable: true,
    styles: {
      control: (provided) => ({
        ...provided,
        border: "none",
        boxShadow: "none",
        background: "transparent",
        minHeight: "48px",
      }),
      input: (provided) => ({
        ...provided,
        color: "inherit",
        ".dark &": { color: "white" },
      }),
      placeholder: (provided) => ({
        ...provided,
        color: "#9CA3AF",
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#F3F4F6" : "transparent",
        color: "inherit",
        ".dark &": {
          backgroundColor: state.isFocused ? "rgba(255, 255, 255, 0.1)" : "transparent",
        },
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: "var(--bg-color, #ffffff)",
        marginTop: "4px",
        borderRadius: "12px",
        border: "1px solid #E5E7EB",
        ".dark &": {
          backgroundColor: "rgba(var(--c-neutral-800), var(--tw-bg-opacity, 1))",
          color: "#ffffff",
          border: "1px solid #374151",
        },
      }),
      singleValue: (provided) => ({
        ...provided,
        color: "inherit",
        ".dark &": { color: "white" },
      }),
    },
  }}
/>
                </div>
                {formik.touched.street && formik.errors.street && (
                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formik.errors.street}
                  </div>
                )}
              </div>
            </div>
          </div>

        {/* Address Details Card */}
<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
    <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
      2
    </span>
    Address Details *
  </h3>

  {/* Row 1: City & Neighborhood */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <FormItem label="City" desc="City or municipality">
      <Input
        name="city"
        value={formik.values.city}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder="e.g., Lima, New York"
        className="text-base"
      />
      {formik.touched.city && formik.errors.city && (
        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {formik.errors.city}
        </div>
      )}
    </FormItem>

    <FormItem label="Neighborhood" desc="District or neighborhood (optional)">
      <Input
        name="neighborhood"
        value={formik.values.neighborhood}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder="e.g., Miraflores, Manhattan"
        className="text-base"
      />
      {formik.touched.neighborhood && formik.errors.neighborhood && (
        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {formik.errors.neighborhood}
        </div>
      )}
    </FormItem>
  </div>

  {/* Row 2: State & Zip */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
    <FormItem label="State/Province" desc="State or province">
      <Input
        name="state"
        value={formik.values.state}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder="e.g., California"
        className="text-base"
      />
      {formik.touched.state && formik.errors.state && (
        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {formik.errors.state}
        </div>
      )}
    </FormItem>

    <FormItem label="Zip/Postal Code" desc="Postal or ZIP code">
      <Input
        name="zipcode"
        value={formik.values.zipcode}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder="e.g., 94102"
        className="text-base"
      />
      {formik.touched.zipcode && formik.errors.zipcode && (
        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {formik.errors.zipcode}
        </div>
      )}
    </FormItem>
  </div>

  {/* Row 3: Latitude & Longitude (Read-only) */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
    <FormItem label="Latitude" desc="Automatically filled from map">
      <Input
        name="latitude"
        value={formik.values.latitude || ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder="38.9072"
        className="text-base bg-gray-50 dark:bg-gray-700/50"
        readOnly
      />
      {formik.touched.latitude && formik.errors.latitude && (
        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {formik.errors.latitude}
        </div>
      )}
    </FormItem>

    <FormItem label="Longitude" desc="Automatically filled from map">
      <Input
        name="longitude"
        value={formik.values.longitude || ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder="-77.0369"
        className="text-base bg-gray-50 dark:bg-gray-700/50"
        readOnly
      />
      {formik.touched.longitude && formik.errors.longitude && (
        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {formik.errors.longitude}
        </div>
      )}
    </FormItem>
  </div>
</div>

          {/* Map Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                3
              </span>
              Pin Your Location
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 ml-10">
              Click on the map to set your exact property location. The red circle shows the approximate area.
            </p>

            <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3 ring-2 ring-gray-200 dark:ring-gray-700 rounded-2xl overflow-hidden shadow-inner">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={13}
                center={userLocation}
                onClick={async (event) => {
                  const newPos = {
                    lat: event.latLng?.lat() ?? 0,
                    lng: event.latLng?.lng() ?? 0,
                  };
                  setUserLocation(newPos);
                  await fetchAddress(newPos.lat, newPos.lng);
                  setInfoOpen(true);
                  setUpdateLocation(true);
                }}
                options={{
                  maxZoom: 15,
                  minZoom: 12,
                  zoomControl: true,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                  disableDefaultUI: true,
                  keyboardShortcuts: false,
                  styles: [
                    {
                      featureType: "all",
                      elementType: "labels",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "road",
                      elementType: "labels",
                      stylers: [{ visibility: "simplified" }, { lightness: 40 }],
                    },
                    {
                      featureType: "road.arterial",
                      elementType: "labels",
                      stylers: [{ visibility: "on" }],
                    },
                    {
                      featureType: "road.highway",
                      elementType: "labels",
                      stylers: [{ visibility: "on" }, { weight: 1.5 }],
                    },
                    {
                      featureType: "road.highway",
                      elementType: "geometry",
                      stylers: [{ visibility: "simplified" }, { lightness: 20 }],
                    },
                    {
                      featureType: "road.arterial",
                      elementType: "geometry",
                      stylers: [{ visibility: "simplified" }, { lightness: 40 }],
                    },
                    {
                      featureType: "road.local",
                      elementType: "geometry",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "poi.business",
                      elementType: "labels",
                      stylers: [{ visibility: "simplified" }],
                    },
                    {
                      featureType: "poi.government",
                      elementType: "labels",
                      stylers: [{ visibility: "on" }],
                    },
                    {
                      featureType: "poi.place_of_worship",
                      elementType: "labels",
                      stylers: [{ visibility: "off" }],
                    },
                    {
                      featureType: "poi.medical",
                      elementType: "labels",
                      stylers: [{ visibility: "simplified" }],
                    },
                    {
                      featureType: "poi.school",
                      elementType: "labels",
                      stylers: [{ visibility: "simplified" }],
                    },
                    {
                      featureType: "landscape",
                      elementType: "geometry",
                      stylers: [{ lightness: 60 }],
                    },
                    {
                      featureType: "transit",
                      elementType: "geometry",
                      stylers: [{ visibility: "off" }],
                    },
                  ],
                }}
              >
                <Marker position={userLocation} onClick={() => setInfoOpen(true)}>
                  {infoOpen && (
                    <InfoWindow position={userLocation} onCloseClick={() => setInfoOpen(false)}>
                      <div className="w-72 bg-white rounded-lg overflow-hidden relative font-sans">
                        <div className="p-3">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1">Property Location</h3>
                          <p className="text-xs text-gray-600">{googleAddress}</p>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
                <Circle
                  center={userLocation}
                  radius={500}
                  options={{
                    fillColor: "#F56948",
                    fillOpacity: 0.15,
                    strokeColor: "#F56948",
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                  }}
                />
              </GoogleMap>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Step 5 of 9 • Set your property location
            </div>
            <div className="flex gap-3">
              <ButtonSecondary
                href={`/add-listing-4/${slug}`}
                disabled={formik.isSubmitting}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Go Back
                </span>
              </ButtonSecondary>
              <ButtonPrimary 
                type="submit" 
                disabled={formik.isSubmitting}
                className="min-w-[140px]"
              >
                {formik.isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continue
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                )}
              </ButtonPrimary>
            </div>
          </div>
        </form>
      </div>
    </CommonLayoutV2>
  );
};

export default Location;